import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, MapPin, FileText, Car, Package, ClipboardList, Sparkles } from 'lucide-react';
import { useAppSystem } from '../../hooks/useAppSystem';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import { Customer } from '../../lib/customers';
import { getStockItems, StockItem } from '../../lib/stockItems';
import toast from 'react-hot-toast';
import { customerHasOpenOpportunity } from '../../lib/crm/opportunityLinking';
import { createLeadForCustomer, emitLeadCreated } from '../../lib/crm/createLead';
import CreateBookingModal from '../CreateBookingModal';
import CreateProposalModal from '../CreateProposalModal';
import CreateMobileBookingModal from '../CreateMobileBookingModal';
import PlaceOrderModal from '../PlaceOrderModal';
import ScheduleFieldQuoteModal from '../ScheduleFieldQuoteModal';

interface CreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer?: Customer | null;
  onCreateAction?: (type: 'booking' | 'mobileBooking' | 'proposal' | 'placeOrder') => void;
}

export default function CreateModal({
  isOpen,
  onClose,
  customer,
  onCreateAction
}: CreateModalProps) {
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isCreateProposalModalOpen, setIsCreateProposalModalOpen] = useState(false);
  const [isCreateMobileBookingModalOpen, setIsCreateMobileBookingModalOpen] = useState(false);
  const [showLocationBooking, setShowLocationBooking] = useState(true);
  const [showMobileBooking, setShowMobileBooking] = useState(true);
  const [showFieldQuotes, setShowFieldQuotes] = useState(false);
  const [isScheduleFieldQuoteOpen, setIsScheduleFieldQuoteOpen] = useState(false);
  const [customerHasOpenLead, setCustomerHasOpenLead] = useState<boolean | null>(null);
  const [creatingLead, setCreatingLead] = useState(false);
  const [isPlaceOrderModalOpen, setIsPlaceOrderModalOpen] = useState(false);
  const [placeOrderItems, setPlaceOrderItems] = useState<StockItem[]>([]);
  const [loadingPlaceOrder, setLoadingPlaceOrder] = useState(false);
  const { coreApps, installedAddons } = useAppSystem();
  const { selectedBusiness } = useBusiness();

  const isSupplier = Boolean(customer?.is_supplier);
  
  // Check if calendar app is installed (required for booking options)
  const isCalendarAppInstalled = coreApps.some(app => app.app?.slug === 'calendar') || 
                                 installedAddons.some(app => app.app?.slug === 'calendar');
  
  // Check if proposals app is installed
  const isProposalsAppInstalled = coreApps.some(app => app.app?.slug === 'proposals') || 
                                  installedAddons.some(app => app.app?.slug === 'proposals');

  // Check if inventory (supplies) app is installed
  const isInventoryAppInstalled = coreApps.some(app => app.app?.slug === 'inventory') ||
                                  installedAddons.some(app => app.app?.slug === 'inventory');

  // Check if CRM app is installed
  const isCrmAppInstalled = coreApps.some(app => app.app?.slug === 'crm') ||
                            installedAddons.some(app => app.app?.slug === 'crm');

  // When customer is supplier and business has inventory app: show only Place order
  const showOnlyPlaceOrder = isSupplier && isInventoryAppInstalled;

  // Whether this customer already has an open CRM lead (null while unresolved).
  const recheckOpenLead = async () => {
    if (!isCrmAppInstalled || !selectedBusiness?.id || !customer?.id || isSupplier) {
      setCustomerHasOpenLead(null);
      return;
    }
    try {
      const hasOpen = await customerHasOpenOpportunity(selectedBusiness.id, customer.id);
      setCustomerHasOpenLead(hasOpen);
    } catch (error) {
      console.error('Error checking open lead:', error);
      setCustomerHasOpenLead(null);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setCustomerHasOpenLead(null);
      return;
    }
    let cancelled = false;
    setCustomerHasOpenLead(null);
    void (async () => {
      if (!cancelled) await recheckOpenLead();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isCrmAppInstalled, selectedBusiness?.id, customer?.id, isSupplier]);

  // Debug logging
  useEffect(() => {
    // Removed debug logging
  }, [isCalendarAppInstalled, isProposalsAppInstalled, showLocationBooking, showMobileBooking, coreApps, installedAddons]);

  // Load calendar app settings for mobile and location bookings
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!selectedBusiness?.id) {
      return;
    }

    const loadCalendarSettings = async () => {
      if (!isCalendarAppInstalled) {
        setShowLocationBooking(false);
        setShowMobileBooking(false);
        setShowFieldQuotes(false);
        return;
      }

      try {
        // Load location booking setting
        const { data: locationData, error: locationError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_location_booking_in_fab',
            p_default_value: true
          });

        if (!locationError && locationData !== null) {
          setShowLocationBooking(locationData);
        } else {
          setShowLocationBooking(true);
        }

        // Load mobile booking setting
        const { data: mobileData, error: mobileError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_mobile_booking_in_fab',
            p_default_value: true
          });

        if (!mobileError && mobileData !== null) {
          setShowMobileBooking(mobileData);
        } else {
          setShowMobileBooking(true);
        }

        // Field quotes (off by default; same setting the FAB uses)
        const { data: fqData, error: fqError } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_field_quotes_in_fab',
            p_default_value: false
          });
        setShowFieldQuotes(!fqError && fqData !== null ? !!fqData : false);
      } catch (error) {
        console.error('Error loading calendar settings:', error);
      }
    };

    loadCalendarSettings();

    // Listen for setting changes
    const handleCalendarSettingChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.key === 'show_location_booking_in_fab') {
        setShowLocationBooking(Boolean(customEvent.detail.value));
      } else if (customEvent.detail?.key === 'show_mobile_booking_in_fab') {
        setShowMobileBooking(Boolean(customEvent.detail.value));
      } else if (customEvent.detail?.key === 'show_field_quotes_in_fab') {
        setShowFieldQuotes(Boolean(customEvent.detail.value));
      }
    };

    window.addEventListener('calendarSettingChanged', handleCalendarSettingChanged as EventListener);
    return () => {
      window.removeEventListener('calendarSettingChanged', handleCalendarSettingChanged as EventListener);
    };
  }, [isOpen, selectedBusiness?.id, isCalendarAppInstalled]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleLocationBookingClick = () => {
    if (onCreateAction) {
      onClose();
      onCreateAction('booking');
      return;
    }
    setIsCreateBookingModalOpen(true);
    setTimeout(() => { onClose(); }, 100);
  };

  const handleProposalClick = () => {
    if (onCreateAction) {
      onClose();
      onCreateAction('proposal');
      return;
    }
    setIsCreateProposalModalOpen(true);
    setTimeout(() => { onClose(); }, 100);
  };

  const handleMobileBookingClick = () => {
    if (onCreateAction) {
      onClose();
      onCreateAction('mobileBooking');
      return;
    }
    setIsCreateMobileBookingModalOpen(true);
    setTimeout(() => { onClose(); }, 100);
  };

  const handleFieldQuoteClick = () => {
    // No parent delegation for field quotes — open the scheduler in place,
    // pre-linked to this conversation's customer.
    setIsScheduleFieldQuoteOpen(true);
    setTimeout(() => { onClose(); }, 100);
  };

  const handleCreateLeadClick = async () => {
    if (!selectedBusiness?.id || !customer?.id || creatingLead) return;
    setCreatingLead(true);
    setCustomerHasOpenLead(true);
    onClose();
    try {
      await createLeadForCustomer(selectedBusiness.id, customer);
      emitLeadCreated(customer.id);
      toast.success('Lead created');
    } catch (error) {
      console.error('Error creating lead:', error);
      const message = error instanceof Error ? error.message : '';
      if (message === 'no-pipeline' || message === 'no-stage') {
        toast.error('Set up a CRM pipeline before creating leads');
      } else {
        toast.error('Could not create lead');
      }
      void recheckOpenLead();
    } finally {
      setCreatingLead(false);
    }
  };

  const handlePlaceOrderClick = async () => {
    if (onCreateAction) {
      onClose();
      onCreateAction('placeOrder');
      return;
    }
    if (!selectedBusiness?.id || !customer?.id) return;
    try {
      setLoadingPlaceOrder(true);
      const items = await getStockItems(selectedBusiness.id);
      setPlaceOrderItems(items);
      setIsPlaceOrderModalOpen(true);
      setTimeout(() => onClose(), 100);
    } catch (err) {
      console.error('Error loading stock items for place order:', err);
    } finally {
      setLoadingPlaceOrder(false);
    }
  };

  return createPortal(
    <>
      {isOpen && (
        <>
          {/* iOS-style backdrop with blur */}
          <div 
            className="fixed inset-0 transition-opacity duration-200"
            style={{
              zIndex: 100065,
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              opacity: 1,
              pointerEvents: 'auto'
            }}
            onClick={onClose}
          />
          
          {/* Modal Content - iOS Style */}
          <div 
            className="fixed inset-0 flex items-center justify-center pointer-events-none z-[100066]"
          >
            <div 
              className="bg-white rounded-[20px] shadow-xl max-w-md w-full mx-4 pointer-events-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Close button - top right corner */}
              <button 
                onClick={onClose} 
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5" />
              </button>

              {/* Content */}
              <div className="px-6 py-5">
                <div className="space-y-3">
                  {showOnlyPlaceOrder ? (
                    <button
                      onClick={handlePlaceOrderClick}
                      disabled={loadingPlaceOrder}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                           style={{ 
                             backdropFilter: 'blur(10px)', 
                             WebkitBackdropFilter: 'blur(10px)'
                           }}>
                        <Package className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900">Place order</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {loadingPlaceOrder ? 'Loading...' : 'Create a new purchase order with this supplier'}
                        </div>
                      </div>
                    </button>
                  ) : (
                    <>
                      {showLocationBooking && (
                        <button
                          onClick={handleLocationBookingClick}
                          className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                               style={{ 
                                 backdropFilter: 'blur(10px)', 
                                 WebkitBackdropFilter: 'blur(10px)'
                               }}>
                            <MapPin className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Location Booking</div>
                            <div className="text-xs text-gray-500 mt-0.5">Create a new location booking</div>
                          </div>
                        </button>
                      )}
                      {showMobileBooking && (
                        <button
                          onClick={handleMobileBookingClick}
                          className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                               style={{ 
                                 backdropFilter: 'blur(10px)', 
                                 WebkitBackdropFilter: 'blur(10px)'
                               }}>
                            <Car className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Mobile Booking</div>
                            <div className="text-xs text-gray-500 mt-0.5">Create a new mobile booking</div>
                          </div>
                        </button>
                      )}
                      {isProposalsAppInstalled && (
                        <button
                          onClick={handleProposalClick}
                          className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                               style={{ 
                                 backdropFilter: 'blur(10px)', 
                                 WebkitBackdropFilter: 'blur(10px)'
                               }}>
                            <FileText className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Proposal</div>
                            <div className="text-xs text-gray-500 mt-0.5">Create a new proposal</div>
                          </div>
                        </button>
                      )}
                      {showFieldQuotes && isCalendarAppInstalled && (
                        <button
                          onClick={handleFieldQuoteClick}
                          className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                               style={{ 
                                 backdropFilter: 'blur(10px)', 
                                 WebkitBackdropFilter: 'blur(10px)'
                               }}>
                            <ClipboardList className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Field Quote</div>
                            <div className="text-xs text-gray-500 mt-0.5">Schedule a field quote for this customer</div>
                          </div>
                        </button>
                      )}
                      {isCrmAppInstalled && customerHasOpenLead === false && (
                        <button
                          onClick={handleCreateLeadClick}
                          className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                               style={{ 
                                 backdropFilter: 'blur(10px)', 
                                 WebkitBackdropFilter: 'blur(10px)'
                               }}>
                            <Sparkles className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Create Lead</div>
                            <div className="text-xs text-gray-500 mt-0.5">Add this customer as a new lead</div>
                          </div>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Create Booking Modal - Always rendered so it can open even when CreateModal is closed */}
      <CreateBookingModal
        isOpen={isCreateBookingModalOpen}
        onClose={() => setIsCreateBookingModalOpen(false)}
        onSuccess={() => {
          setIsCreateBookingModalOpen(false);
        }}
        initialCustomer={customer || undefined}
      />
      
      {/* Create Mobile Booking Modal - Always rendered so it can open even when CreateModal is closed */}
      <CreateMobileBookingModal
        isOpen={isCreateMobileBookingModalOpen}
        onClose={() => setIsCreateMobileBookingModalOpen(false)}
        onSuccess={() => {
          setIsCreateMobileBookingModalOpen(false);
        }}
        prefilledData={customer ? { customer } : undefined}
      />
      
      {/* Create Proposal Modal - Always rendered so it can open even when CreateModal is closed */}
      <CreateProposalModal
        isOpen={isCreateProposalModalOpen}
        onClose={() => setIsCreateProposalModalOpen(false)}
        onSuccess={() => {
          setIsCreateProposalModalOpen(false);
        }}
        initialCustomer={customer || undefined}
      />

      {/* Place order modal (same as Inventory page) - when customer is supplier and inventory app is installed */}
      <PlaceOrderModal
        isOpen={isPlaceOrderModalOpen}
        onClose={() => setIsPlaceOrderModalOpen(false)}
        items={placeOrderItems}
      />

      {/* Field quote scheduler — pre-linked to this conversation's customer */}
      <ScheduleFieldQuoteModal
        isOpen={isScheduleFieldQuoteOpen}
        onClose={() => setIsScheduleFieldQuoteOpen(false)}
        projectId={null}
        projectTitle={null}
        initialCustomer={customer || null}
        onSuccess={() => setIsScheduleFieldQuoteOpen(false)}
      />

    </>,
    document.body
  );
}

